import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, BookOpen, Search } from "lucide-react";
import { apiFetch, API } from "@/utils/api";

function formatCurrency(amount) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

function TypeBadge({ type }) {
  const styles = {
    asset: "bg-blue-100 text-blue-700",
    liability: "bg-rose-100 text-rose-700",
    equity: "bg-purple-100 text-purple-700",
    income: "bg-emerald-100 text-emerald-700",
    expense: "bg-amber-100 text-amber-700",
  };

  const labels = {
    asset: "Activo",
    liability: "Pasivo",
    equity: "Patrimonio",
    income: "Ingreso",
    expense: "Gasto",
  };

  return (
    <Badge className={styles[type] || "bg-slate-100"} variant="secondary">
      {labels[type] || type}
    </Badge>
  );
}

function AccountForm({ onSave, onCancel }) {
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    type: "expense",
    parent_code: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.code || !formData.name) {
      toast.error("Código y nombre son obligatorios");
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Código PGC *</Label>
          <Input
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            placeholder="Ej: 6250001"
            maxLength={10}
            data-testid="account-code-input"
          />
          <p className="text-xs text-slate-500 mt-1">
            Usa subcuentas para mayor detalle (ej: 6280001)
          </p>
        </div>
        <div>
          <Label>Tipo *</Label>
          <Select
            value={formData.type}
            onValueChange={(value) => setFormData({ ...formData, type: value })}
          >
            <SelectTrigger data-testid="account-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asset">Activo (Grupo 2-5)</SelectItem>
              <SelectItem value="liability">Pasivo (Grupo 4-5)</SelectItem>
              <SelectItem value="equity">Patrimonio (Grupo 1)</SelectItem>
              <SelectItem value="expense">Gasto (Grupo 6)</SelectItem>
              <SelectItem value="income">Ingreso (Grupo 7)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Nombre de la cuenta *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ej: Seguros de responsabilidad civil"
          data-testid="account-name-input"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" data-testid="save-account-btn">
          Crear Cuenta
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function ChartOfAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const fetchAccounts = async () => {
    try {
      const response = await apiFetch(`${API}/accounting/chart`);
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleSaveAccount = async (formData) => {
    try {
      const response = await apiFetch(`${API}/accounting/chart`, {
        method: "POST",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Cuenta creada");
        setDialogOpen(false);
        fetchAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al crear cuenta");
      }
    } catch (error) {
      toast.error("Error al crear cuenta");
    }
  };

  const filteredAccounts = accounts.filter((acc) => {
    const matchesSearch =
      !search ||
      acc.code.includes(search) ||
      acc.name.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === "all" || acc.type === typeFilter;
    return matchesSearch && matchesType;
  });

  // Group accounts by first digit
  const groupedAccounts = filteredAccounts.reduce((groups, acc) => {
    const group = acc.code[0];
    if (!groups[group]) groups[group] = [];
    groups[group].push(acc);
    return groups;
  }, {});

  const groupNames = {
    "1": "Financiación básica",
    "2": "Activo no corriente",
    "3": "Existencias",
    "4": "Acreedores y deudores",
    "5": "Cuentas financieras",
    "6": "Compras y gastos",
    "7": "Ventas e ingresos",
  };

  return (
    <div className="space-y-6" data-testid="chart-accounts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Plan de Cuentas
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Plan General Contable - {accounts.length} cuentas
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="new-account-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Cuenta
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label className="mb-2 block">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Código o nombre..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-accounts-input"
                />
              </div>
            </div>
            <div className="w-48">
              <Label className="mb-2 block">Tipo</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="account-type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="asset">Activo</SelectItem>
                  <SelectItem value="liability">Pasivo</SelectItem>
                  <SelectItem value="equity">Patrimonio</SelectItem>
                  <SelectItem value="expense">Gasto</SelectItem>
                  <SelectItem value="income">Ingreso</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts by Group */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="spinner" />
        </div>
      ) : (
        <div className="space-y-6">
          {Object.keys(groupedAccounts)
            .sort()
            .map((group) => (
              <Card key={group}>
                <CardHeader className="py-3 bg-slate-50">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <span className="text-blue-600 font-mono">Grupo {group}</span>
                    <span className="text-slate-600">{groupNames[group]}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table className="table-dense">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[100px]">Código</TableHead>
                        <TableHead>Nombre</TableHead>
                        <TableHead className="w-[100px]">Tipo</TableHead>
                        <TableHead className="text-right w-[150px]">Saldo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groupedAccounts[group].map((acc) => (
                        <TableRow key={acc.account_id} data-testid={`account-row-${acc.code}`}>
                          <TableCell className="font-mono font-medium text-blue-600">
                            {acc.code}
                          </TableCell>
                          <TableCell>{acc.name}</TableCell>
                          <TableCell>
                            <TypeBadge type={acc.type} />
                          </TableCell>
                          <TableCell
                            className={`numeric ${
                              acc.balance < 0 ? "negative" : acc.balance > 0 ? "positive" : ""
                            }`}
                          >
                            {formatCurrency(acc.balance)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      {/* New Account Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nueva Cuenta Contable</DialogTitle>
          </DialogHeader>
          <AccountForm
            onSave={handleSaveAccount}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
